import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import MetricCard from './components/MetricCard';
import AlertFeed from './components/AlertFeed';
import TopViolatorsList from './components/TopViolatorsList';
import FilterControls from './components/FilterControls';
import { dashboardService } from '../../services/supabaseService';

const ComplianceOverview = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('last30days');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(true);
  
  // Real data state
  const [metrics, setMetrics] = useState({
    overallComplianceScore: 0,
    totalActiveViolations: 0,
    highRiskEmployees: 0,
    complianceCostImpact: 0
  });
  const [alertsData, setAlertsData] = useState([]);
  const [topViolatorsData, setTopViolatorsData] = useState([]);

  // Fetch all dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const filters = { department: selectedDepartment, period: selectedPeriod };
      
      // Fetch essential data only
      const [metricsData, alertsData, violatorsData] = await Promise.all([
        dashboardService?.getDashboardMetrics(filters),
        dashboardService?.getRecentAlerts(filters),
        dashboardService?.getTopViolators(filters)
      ]);

      setMetrics(metricsData);
      setAlertsData(alertsData);
      setTopViolatorsData(violatorsData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load and when filters change
  useEffect(() => {
    fetchDashboardData();
  }, [selectedDepartment, selectedPeriod]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        console.log("Auto-refresh triggered at:", new Date()?.toLocaleTimeString());
        fetchDashboardData();
      }, 900000); // 15 minutes
      return () => clearInterval(interval);
    }
  }, [autoRefresh, selectedDepartment, selectedPeriod]);

  // Format KPI metrics for display
  const kpiMetrics = [
    {
      title: "Overall Compliance Score",
      value: metrics?.overallComplianceScore > 0 ? `${metrics?.overallComplianceScore?.toFixed(1)}%` : "0%",
      change: metrics?.overallComplianceScore > 0 ? "Based on current data" : "No data yet",
      changeType: metrics?.overallComplianceScore >= 85 ? "positive" : metrics?.overallComplianceScore >= 70 ? "neutral" : "negative",
      icon: "public/assets/images/Real_logo-1771587686730.png",
      severity: metrics?.overallComplianceScore >= 85 ? "success" : metrics?.overallComplianceScore >= 70 ? "warning" : "error"
    },
    {
      title: "Total Active Violations",
      value: metrics?.totalActiveViolations?.toString(),
      change: metrics?.totalActiveViolations > 0 ? `${metrics?.totalActiveViolations} violations recorded` : "No violations yet",
      changeType: metrics?.totalActiveViolations === 0 ? "positive" : "negative",
      icon: "AlertTriangle",
      severity: metrics?.totalActiveViolations === 0 ? "success" : metrics?.totalActiveViolations < 10 ? "warning" : "error"
    },
    {
      title: "High-Risk Employees",
      value: metrics?.highRiskEmployees?.toString(),
      change: metrics?.highRiskEmployees > 0 ? `${metrics?.highRiskEmployees} employees at risk` : "No high-risk employees",
      changeType: metrics?.highRiskEmployees === 0 ? "positive" : "negative",
      icon: "Users",
      severity: metrics?.highRiskEmployees === 0 ? "success" : metrics?.highRiskEmployees < 5 ? "warning" : "error"
    },
    {
      title: "Compliance Cost Impact",
      value: `$${(metrics?.complianceCostImpact / 1000)?.toFixed(1)}K`,
      change: metrics?.complianceCostImpact > 0 ? `Total cost impact` : "No cost impact",
      changeType: metrics?.complianceCostImpact === 0 ? "positive" : "negative",
      icon: "DollarSign",
      severity: "default"
    }
  ];

  const handleExport = () => {
    alert("Export functionality: Dashboard data would be exported to PDF/Excel format with current filters applied.");
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
          <Breadcrumb />
          
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
              Compliance Overview
            </h1>
            <p className="text-sm md:text-base lg:text-lg font-caption text-muted-foreground">
              Real-time monitoring of organizational compliance health
            </p>
          </div>

          <FilterControls
            selectedDepartment={selectedDepartment}
            onDepartmentChange={setSelectedDepartment}
            selectedPeriod={selectedPeriod}
            onPeriodChange={setSelectedPeriod}
            autoRefresh={autoRefresh}
            onAutoRefreshToggle={() => setAutoRefresh(!autoRefresh)}
            onExport={handleExport}
          />

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading dashboard data...</p>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
                {kpiMetrics?.map((metric, index) => (
                  <MetricCard key={index} {...metric} />
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                <AlertFeed alerts={alertsData} />
                <TopViolatorsList violators={topViolatorsData} />
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ComplianceOverview;